import React, { useState } from "react";
import {
  FiPlus,
  FiTrash2,
  FiUpload,
  FiCheck,
  FiFileText,
  FiUser,
  FiBookOpen,
  FiLayers,
  FiPaperclip,
} from "react-icons/fi";
import "../styles/ApplyForm.css";
import { submitApplication } from "../api";

export default function ApplyForm({ onSubmitExternal }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    qualification: "BE",
    graduationYear: new Date().getFullYear(),
    aggregate: "",
    programmingLanguages: [],
    projects: [],
    certifications: [],
    familiarHTML: false,
    familiarCSS: false,
    familiarGit: false,
    experienceYears: 0,
    city: "Pune",
  });
  const [resume, setResume] = useState(null);
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Programming languages: toggle in array
    if (name === "programmingLanguages") {
      const val = value;
      setForm((prev) => {
        const exists = prev.programmingLanguages.includes(val);
        return {
          ...prev,
          programmingLanguages: exists
            ? prev.programmingLanguages.filter((p) => p !== val)
            : [...prev.programmingLanguages, val],
        };
      });
      return;
    }

    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addProject = () =>
    setForm((p) => ({
      ...p,
      projects: [...p.projects, { title: "", description: "", link: "" }],
    }));

  const updateProject = (idx, key, val) =>
    setForm((p) => ({
      ...p,
      projects: p.projects.map((pr, i) =>
        i === idx ? { ...pr, [key]: val } : pr
      ),
    }));

  const removeProject = (idx) =>
    setForm((p) => ({
      ...p,
      projects: p.projects.filter((_, i) => i !== idx),
    }));

  const addCertification = () =>
    setForm((p) => ({
      ...p,
      certifications: [...p.certifications, { name: "", issuer: "", year: "" }],
    }));

  const updateCertification = (idx, key, val) =>
    setForm((p) => ({
      ...p,
      certifications: p.certifications.map((c, i) =>
        i === idx ? { ...c, [key]: val } : c
      ),
    }));

  const removeCertification = (idx) =>
    setForm((p) => ({
      ...p,
      certifications: p.certifications.filter((_, i) => i !== idx),
    }));

  // --------- File handler (PDF + 100KB limit) ----------
  const handleFile = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setResume(null);
      return;
    }

    if (f.type !== "application/pdf") {
      setMessage({ type: "danger", text: "Please upload a PDF resume only." });
      setResume(null);
      return;
    }

    const MAX_BYTES = 100 * 1024;
    if (f.size > MAX_BYTES) {
      setMessage({
        type: "danger",
        text: `Resume too large. Maximum allowed size is 100 KB. Your file is ${(
          f.size / 1024
        ).toFixed(1)} KB.`,
      });
      setResume(null);
      return;
    }

    setResume(f);
    setMessage(null);
  };
  // -----------------------------------------------------

  const clearResume = () => setResume(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    if (!form.fullName || !form.email || !form.phone || !form.aggregate) {
      setMessage({ type: "danger", text: "Please fill required fields." });
      setSubmitting(false);
      window.scrollTo(0, 0);
      return;
    }

    if (!resume) {
      setMessage({
        type: "danger",
        text: "Resume is mandatory. Please upload your resume in PDF format (max 100 KB).",
      });
      setSubmitting(false);
      window.scrollTo(0, 0);
      return;
    }

    const fd = new FormData();
    Object.keys(form).forEach((k) => {
      const val = form[k];
      if (k === "projects" || k === "certifications") {
        fd.append(k, JSON.stringify(val || []));
      } else if (Array.isArray(val)) {
        val.forEach((v) => fd.append(k, v));
      } else {
        fd.append(k, val);
      }
    });
    if (resume) fd.append("resume", resume);

    try {
      const res = await submitApplication(fd);

      const eligible = res?.data?.eligible ?? false;
      const msg = res?.data?.message;
      setMessage({ type: eligible ? "success" : "warning", text: msg });
      window.scrollTo(0, 0);

      setForm({
        fullName: "",
        email: "",
        phone: "",
        qualification: "BE",
        graduationYear: new Date().getFullYear(),
        aggregate: "",
        programmingLanguages: [],
        projects: [],
        certifications: [],
        familiarHTML: false,
        familiarCSS: false,
        familiarGit: false,
        experienceYears: 0,
        city: "Pune",
      });
      setResume(null);
    } catch (err) {
      let serverMsg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Submission failed";

      if (typeof serverMsg === "object") {
        serverMsg = serverMsg.error || JSON.stringify(serverMsg);
      }

      setMessage({ type: "danger", text: serverMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const LANGS = ["JavaScript", "Python", "PHP", "Java", "C#", "C++"];

  return (
    <div className="apply-page">
      <div className="apply-layout">
        {/* LEFT: MAIN FORM */}
        <main className="apply-main">
          <header className="apply-main-header">
            <div>
              <h2>Student Application</h2>
              <p>Tell us about yourself and your experience in detail.</p>
            </div>
            {/* <div className="apply-step-strip">
              <span className="step active">
                <span className="step-dot" /> Personal
              </span>
              <span className="step active">
                <span className="step-dot" /> Education
              </span>
              <span className="step active">
                <span className="step-dot" /> Skills
              </span>
              <span className="step active">
                <span className="step-dot" /> Projects
              </span>
              <span className="step active">
                <span className="step-dot" /> Resume
              </span>
            </div> */}
          </header>

          {message && (
            <div className={`msg msg-${message.type}`}>{message.text}</div>
          )}

          <form className="apply-form" onSubmit={handleSubmit}>
            {/* SECTION 1: PERSONAL */}
            <section className="form-section">
              <div className="section-header">
                <div className="section-icon-wrap">
                  <FiUser />
                </div>
                <div>
                  <h3>Personal details</h3>
                  <p>Basic information so we can contact you.</p>
                </div>
              </div>

              <div className="field-grid-2">
                <div className="field">
                  <label className="field-label">
                    Full Name <span>*</span>
                  </label>
                  <input
                    className="input"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="field">
                  <label className="field-label">
                    Email <span>*</span>
                  </label>
                  <input
                    className="input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="example@domain.com"
                  />
                </div>
              </div>

              <div className="field-grid-3">
                <div className="field">
                  <label className="field-label">
                    Phone <span>*</span>
                  </label>
                  <input
                    className="input"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div className="field">
                  <label className="field-label">City</label>
                  <input
                    className="input"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                  />
                </div>

                <div className="field">
                  <label className="field-label">Years of Experience</label>
                  <input
                    type=""
                    step="0.1"
                    min="0"
                    max="50"
                    className="input"
                    name="experienceYears"
                    value={form.experienceYears}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </div>
              </div>
            </section>

            {/* SECTION 2: EDUCATION */}
            <section className="form-section">
              <div className="section-header">
                <div className="section-icon-wrap">
                  <FiBookOpen />
                </div>
                <div>
                  <h3>Education</h3>
                  <p>Share your qualification and academic performance.</p>
                </div>
              </div>

              <div className="field-grid-3">
                <div className="field">
                  <label className="field-label">Qualification</label>
                  <select
                    className="input"
                    name="qualification"
                    value={form.qualification}
                    onChange={handleChange}
                  >
                    <option>BE</option>
                    <option>BTech</option>
                    <option>BSc</option>
                    <option>BCA</option>
                    <option>MCA</option>
                    <option>Other</option>
                  </select>
                </div>

                <div className="field">
                  <label className="field-label">Graduation Year</label>
                  <input
                    type=""
                    className="input"
                    name="graduationYear"
                    value={form.graduationYear}
                    onChange={handleChange}
                  />
                </div>

                <div className="field">
                  <label className="field-label">
                    Aggregate % / CGPA <span>*</span>
                  </label>
                  <input
                    className="input"
                    name="aggregate"
                    value={form.aggregate}
                    onChange={handleChange}
                    placeholder="e.g., 72 or 8.2"
                  />
                  <p className="hint">
                    If you enter CGPA (0–10), it will be converted to
                    percentage.
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION 3: SKILLS */}
            <section className="form-section">
              <div className="section-header">
                <div className="section-icon-wrap">
                  <FiLayers />
                </div>
                <div>
                  <h3>Skills</h3>
                  <p>Highlight your core technical strengths.</p>
                </div>
              </div>

              {/* NOTE BLOCK – requirements to get shortlisted */}
              <div className="note-card">
                <strong>
                  To increase your chances of getting shortlisted:
                </strong>
                <ul className="note-list">
                  <li>
                    Basic knowledge of <b>JavaScript</b>, <b>Python</b>, or{" "}
                    <b>PHP</b> (any one)
                  </li>
                  <li>
                    Familiarity with <b>HTML</b>, <b>CSS</b>, and <b>Git</b>
                  </li>
                </ul>
              </div>

              {/* Programming Languages */}
              <div className="field">
                <label className="field-label">
                  Programming Languages (choose at least one)
                </label>
                <div className="lang-list pill-row">
                  {LANGS.map((l) => {
                    const active = form.programmingLanguages.includes(l);
                    return (
                      <button
                        key={l}
                        type="button"
                        className={`pill-pill ${active ? "pill-active" : ""}`}
                        onClick={() =>
                          handleChange({
                            target: { name: "programmingLanguages", value: l },
                          })
                        }
                      >
                        <FiCheck className="pill-icon" />
                        <span>{l}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Familiar With – same pill design */}
              <div className="field">
                <label className="field-label">Familiar With</label>
                <div className="pill-row">
                  <label
                    className={`pill-pill ${
                      form.familiarHTML ? "pill-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="familiarHTML"
                      checked={form.familiarHTML}
                      onChange={handleChange}
                    />
                    <FiCheck className="pill-icon" />
                    <span>HTML</span>
                  </label>

                  <label
                    className={`pill-pill ${
                      form.familiarCSS ? "pill-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="familiarCSS"
                      checked={form.familiarCSS}
                      onChange={handleChange}
                    />
                    <FiCheck className="pill-icon" />
                    <span>CSS</span>
                  </label>

                  <label
                    className={`pill-pill ${
                      form.familiarGit ? "pill-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="familiarGit"
                      checked={form.familiarGit}
                      onChange={handleChange}
                    />
                    <FiCheck className="pill-icon" />
                    <span>Git</span>
                  </label>
                </div>
              </div>
            </section>

            {/* SECTION 4: PROJECTS & CERTIFICATIONS */}
            <section className="form-section">
              <div className="section-header">
                <div className="section-icon-wrap">
                  <FiPaperclip />
                </div>
                <div>
                  <h3>Projects & Certifications</h3>
                  <p>Showcase real work and achievements.</p>
                </div>
              </div>

              {/* Projects */}
              <div className="subsection-heading">
                <div>
                  <h4>Projects (optional)</h4>
                  <p>Add real or academic projects with links.</p>
                </div>
                <button
                  type="button"
                  className="chip-btn primary"
                  onClick={addProject}
                >
                  <FiPlus /> Add Project
                </button>
              </div>

              <div className="card-stack">
                {form.projects.length === 0 && (
                  <div className="empty-card">
                    No projects added yet — click “Add Project”.
                  </div>
                )}
                {form.projects.map((p, idx) => (
                  <article className="item-card" key={idx}>
                    <header className="item-card-header">
                      <span className="item-index">Project {idx + 1}</span>
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => removeProject(idx)}
                        title="Remove project"
                      >
                        <FiTrash2 />
                      </button>
                    </header>

                    <div className="field">
                      <label className="field-label">Title</label>
                      <input
                        className="input"
                        placeholder="Project title"
                        value={p.title}
                        onChange={(e) =>
                          updateProject(idx, "title", e.target.value)
                        }
                      />
                    </div>

                    <div className="field">
                      <label className="field-label">Short Description</label>
                      <input
                        className="input"
                        placeholder="What did you build? What problem did it solve?"
                        value={p.description}
                        onChange={(e) =>
                          updateProject(idx, "description", e.target.value)
                        }
                      />
                    </div>

                    <div className="field">
                      <label className="field-label">
                        Link (GitHub / Demo)
                      </label>
                      <input
                        className="input"
                        placeholder="https://github.com/..."
                        value={p.link}
                        onChange={(e) =>
                          updateProject(idx, "link", e.target.value)
                        }
                      />
                    </div>
                  </article>
                ))}
              </div>

              {/* Certifications */}
              <div className="subsection-heading top-gap">
                <div>
                  <h4>Certifications (optional)</h4>
                  <p>Include relevant online or academic certificates.</p>
                </div>
                <button
                  type="button"
                  className="chip-btn primary"
                  onClick={addCertification}
                >
                  <FiPlus /> Add Certification
                </button>
              </div>

              <div className="card-stack">
                {form.certifications.length === 0 && (
                  <div className="empty-card">No certifications added yet.</div>
                )}
                {form.certifications.map((c, idx) => (
                  <article className="item-card" key={idx}>
                    <header className="item-card-header">
                      <span className="item-index">
                        Certification {idx + 1}
                      </span>
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => removeCertification(idx)}
                        title="Remove certification"
                      >
                        <FiTrash2 />
                      </button>
                    </header>

                    <div className="field-grid-3">
                      <div className="field">
                        <label className="field-label">Name</label>
                        <input
                          className="input"
                          placeholder="Certification name"
                          value={c.name}
                          onChange={(e) =>
                            updateCertification(idx, "name", e.target.value)
                          }
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Issuer</label>
                        <input
                          className="input"
                          placeholder="Platform / Institute"
                          value={c.issuer}
                          onChange={(e) =>
                            updateCertification(idx, "issuer", e.target.value)
                          }
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Year</label>
                        <input
                          type=""
                          className="input"
                          placeholder="2024"
                          value={c.year}
                          onChange={(e) =>
                            updateCertification(idx, "year", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* SECTION 5: RESUME & SUBMIT */}
            <section className="form-section">
              <div className="section-header">
                <div className="section-icon-wrap">
                  <FiFileText />
                </div>
                <div>
                  <h3>Resume</h3>
                  <p>Attach a concise, updated resume in PDF format.</p>
                </div>
              </div>

              <div className="field">
                <label className="field-label">
                  Upload Resume (PDF) <span>*</span>
                </label>
                <div className="file-row">
                  <label className="file-btn">
                    <FiUpload className="file-icon" />
                    <span>{resume ? resume.name : "Choose PDF file"}</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFile}
                    />
                  </label>
                  <p className="hint">Max size: 100 KB. PDF only.</p>
                </div>

                {resume && (
                  <div className="file-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() =>
                        window.open(URL.createObjectURL(resume), "_blank")
                      }
                    >
                      <FiFileText /> Preview
                    </button>
                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={clearResume}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="submit-row">
                <div className="submit-text">
                  <strong>Review before submit:</strong>
                  <p>Make sure your email, phone, and aggregate are correct.</p>
                </div>
                <button className="btn-primary" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </section>
          </form>
        </main>
      </div>
    </div>
  );
}
